import { paymentRecipientFieldsSchema } from "@ai-crm/shared";
import type {
  PaymentRecipientFields,
  PaymentRecipientPreviewDTO,
} from "@ai-crm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "../components/confirm-dialog";
import { PageHeader } from "../components/layout";
import { Pagination } from "../components/pagination";
import { adminApi, ApiError } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatMoscowDateTime } from "../lib/format";
import { usePaymentDocuments } from "../lib/payment-documents";
import {
  paymentButton,
  paymentPrimary,
  paymentError,
  paymentKeys,
  usePaymentScope,
  usePaymentWrite,
  usePreviewExpiry,
} from "../lib/payment-ui";

const emptyFields: PaymentRecipientFields = {
  name: "",
  inn: "",
  taxStatus: "NPD",
  address: "",
  email: "",
  bankName: "",
  bik: "",
  bankAccount: "",
  correspondentAccount: "",
};
const labels: Record<keyof PaymentRecipientFields, string> = {
  name: "ФИО получателя",
  inn: "ИНН получателя",
  taxStatus: "Налоговый статус",
  address: "Адрес",
  email: "Email",
  bankName: "Банк",
  bik: "БИК",
  bankAccount: "Расчётный счёт",
  correspondentAccount: "Корреспондентский счёт",
};
export function PaymentRecipientPage() {
  const { token, admin } = useAdminAuth();
  return <RecipientWorkspace key={`${admin?.id}:${token}`} />;
}
function RecipientWorkspace() {
  const scope = usePaymentScope(),
    write = usePaymentWrite(),
    docs = usePaymentDocuments(),
    client = useQueryClient();
  const [fields, setFields] = useState<PaymentRecipientFields>(emptyFields);
  const [baseId, setBaseId] = useState<string | null>(null);
  const initialized = useRef(false);
  const [preview, setPreview] = useState<PaymentRecipientPreviewDTO | null>(
    null,
  );
  const [sampleToken, setSampleToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const previewPending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.at(-1);
  const expired = usePreviewExpiry(preview?.expiresAt);
  const current = useQuery({
    queryKey: paymentKeys.recipient(scope.adminId),
    queryFn: ({ signal }) =>
      scope.read(signal, () => adminApi.paymentRecipient(scope.token!, signal)),
    enabled: Boolean(scope.token),
  });
  const history = useQuery({
    queryKey: [
      ...paymentKeys.recipient(scope.adminId),
      "versions",
      cursor ?? "",
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.paymentRecipientVersions(
          scope.token!,
          { cursor, limit: 10 },
          signal,
        ),
      ),
    enabled: Boolean(scope.token),
  });
  useEffect(() => {
    if (initialized.current || !current.data) return;
    initialized.current = true;
    const active = current.data.activeVersion;
    if (active) {
      const next = { ...emptyFields };
      for (const key of Object.keys(next) as (keyof PaymentRecipientFields)[]) {
        if (key !== "taxStatus") next[key] = active[key];
      }
      setFields(next);
      setBaseId(active.id);
    }
  }, [current.data]);
  const locked = loading || write.locked || docs.busy;
  async function requestPreview() {
    if (!scope.token || locked || previewPending.current || !current.data)
      return;
    const parsed = paymentRecipientFieldsSchema.safeParse(fields);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        errors[String(i.path[0])] =
          "Проверьте значение и контрольные цифры реквизитов.";
      });
      setErrors(errors);
      return;
    }
    const guard = scope.capture();
    previewPending.current = true;
    setLoading(true);
    setError(null);
    setErrors({});
    setPreview(null);
    setSampleToken(null);
    const expectedActiveVersionId = conflict
      ? (current.data.activeVersion?.id ?? null)
      : baseId;
    try {
      const result = await adminApi.previewPaymentRecipient(scope.token, {
        expectedActiveVersionId,
        fields: parsed.data,
      });
      if (guard.isCurrent()) {
        setPreview(result);
        setBaseId(result.expectedActiveVersionId);
        setConflict(false);
      }
    } catch (caught) {
      if (guard.isCurrent()) {
        setError(paymentError(caught));
        if (caught instanceof ApiError && caught.status === 409) {
          setConflict(true);
          await current.refetch();
        }
      }
    } finally {
      if (guard.isCurrent()) {
        setLoading(false);
        previewPending.current = false;
      }
    }
  }
  async function sample(open: boolean) {
    if (!preview || !scope.token || docs.busy || expired) return;
    const guard = scope.capture(),
      token = preview.previewToken;
    const ok = await docs.fetchDocument(
      () =>
        adminApi.paymentRecipientPreviewPdf(scope.token!, {
          previewToken: token,
        }),
      "recipient-sample.pdf",
      open,
    );
    if (guard.isCurrent() && ok) setSampleToken(token);
  }
  async function confirm() {
    if (
      !scope.token ||
      !preview ||
      sampleToken !== preview.previewToken ||
      expired ||
      locked
    )
      return;
    const guard = scope.capture();
    const token = scope.token,
      fixed = preview;
    const input = {
      requestId: crypto.randomUUID(),
      expectedActiveVersionId: fixed.expectedActiveVersionId,
      previewToken: fixed.previewToken,
    };
    setDialog(false);
    await write.run(
      input,
      (body) => adminApi.confirmPaymentRecipient(token, body),
      async () => {
        setFields(fixed.fields);
        setPreview(null);
        setSampleToken(null);
        setCursorStack([]);
        setMessage(
          "Реквизиты сохранены. Изменения действуют только для новых счетов.",
        );
        const refreshed = await current.refetch();
        if (!guard.isCurrent()) return;
        setBaseId(refreshed.data?.activeVersion?.id ?? null);
        await client.invalidateQueries({
          queryKey: paymentKeys.recipient(scope.adminId),
        });
      },
      async (caught) => {
        if (caught instanceof ApiError && caught.status === 409) {
          setConflict(true);
          setPreview(null);
          setSampleToken(null);
          await current.refetch();
        }
      },
    );
  }
  const field = (key: Exclude<keyof PaymentRecipientFields, "taxStatus">) => (
    <label
      key={key}
      className={`block space-y-1 text-sm ${key === "address" || key === "bankName" ? "sm:col-span-2" : ""}`}
    >
      <span>{labels[key]}</span>
      <input
        className="control"
        value={fields[key]}
        readOnly={
          Boolean(current.data?.activeVersion) &&
          (key === "name" || key === "inn")
        }
        disabled={locked}
        inputMode={
          ["inn", "bik", "bankAccount", "correspondentAccount"].includes(key)
            ? "numeric"
            : undefined
        }
        onChange={(e) => {
          setFields((old) => ({ ...old, [key]: e.target.value }));
          setPreview(null);
          setSampleToken(null);
          setMessage(null);
          setErrors({});
        }}
        aria-invalid={Boolean(errors[key])}
      />
      {errors[key] && (
        <span role="alert" className="text-danger">
          {errors[key]}
        </span>
      )}
    </label>
  );
  return (
    <div>
      <PageHeader
        title="Реквизиты получателя"
        description="Один получатель НПД. Выданные счета сохраняют прежние реквизиты."
      />
      <div className="max-w-4xl space-y-4">
        {current.isPending && <p role="status">Загружаем реквизиты…</p>}
        {current.isError && (
          <p role="alert">
            Не удалось загрузить реквизиты.{" "}
            <button
              className={paymentButton}
              onClick={() => void current.refetch()}
            >
              Повторить
            </button>
          </p>
        )}
        {current.data && (
          <section className="panel space-y-4 p-4" aria-busy={locked}>
            {!current.data.activeVersion && (
              <p>
                Реквизиты ещё не заполнены. Выдача счетов недоступна до
                сохранения.
              </p>
            )}
            <h2 className="text-lg font-semibold">Получатель</h2>
            <p className="text-sm">Налоговый статус: НПД (самозанятый).</p>
            {current.data.activeVersion && (
              <p className="text-sm text-muted-foreground">
                Смена юридического получателя (ФИО/ИНН) требует отдельного
                согласования и сверки оферты.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {(["name", "inn", "address", "email"] as const).map(field)}
            </div>
            <h2 className="text-lg font-semibold">Банк</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  "bankName",
                  "bik",
                  "bankAccount",
                  "correspondentAccount",
                ] as const
              ).map(field)}
            </div>
            {conflict && (
              <div className="space-y-2 rounded-md border p-3 text-sm">
                <p>Активная версия изменилась; ваш ввод сохранён.</p>
                {current.data.activeVersion && (
                  <dl>
                    {Object.entries(labels).map(([key, label]) => (
                      <div key={key} className="break-words">
                        <dt>{label}</dt>
                        <dd>
                          {
                            current.data!.activeVersion![
                              key as keyof PaymentRecipientFields
                            ]
                          }
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
            <button
              className={paymentButton}
              disabled={locked}
              onClick={() => void requestPreview()}
            >
              {conflict
                ? "Повторить предпросмотр с актуальной версией"
                : "Предпросмотр реквизитов"}
            </button>
            {preview && (
              <section
                className="space-y-3 rounded-md border p-3"
                aria-label="Предпросмотр реквизитов"
              >
                <h3 className="font-semibold">Нормализованные реквизиты</h3>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  {Object.entries(labels).map(([key, label]) => (
                    <div key={key} className="break-words">
                      <dt className="text-muted-foreground">
                        {label}
                        {current.data?.activeVersion?.[
                          key as keyof PaymentRecipientFields
                        ] !==
                        preview.fields[key as keyof PaymentRecipientFields]
                          ? " · изменено"
                          : ""}
                      </dt>
                      <dd>
                        {preview.fields[key as keyof PaymentRecipientFields]}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="text-sm">
                  Предпросмотр до {formatMoscowDateTime(preview.expiresAt)} МСК.
                  Образец не предназначен для оплаты.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={paymentButton}
                    disabled={locked || expired}
                    onClick={() => void sample(true)}
                  >
                    Открыть образец PDF
                  </button>
                  <button
                    className={paymentButton}
                    disabled={locked || expired}
                    onClick={() => void sample(false)}
                  >
                    Скачать образец PDF
                  </button>
                </div>
                {expired && !write.uncertain && (
                  <p>Предпросмотр истёк. Получите новый.</p>
                )}
                {!write.uncertain && (
                  <button
                    className={paymentPrimary}
                    disabled={
                      locked || expired || sampleToken !== preview.previewToken
                    }
                    onClick={() => setDialog(true)}
                  >
                    Сохранить реквизиты
                  </button>
                )}
              </section>
            )}
            {dialog && (
              <ConfirmDialog
                title="Сохранить реквизиты получателя"
                description="Изменения только для новых счетов. Выданные документы сохранят прежнюю версию."
                confirmLabel="Сохранить"
                isPending={write.pending}
                onCancel={() => setDialog(false)}
                onConfirm={() => void confirm()}
              />
            )}
            {write.uncertain && (
              <button
                className={paymentPrimary}
                disabled={write.pending}
                onClick={() => void write.retry()}
              >
                Повторить сохранение реквизитов
              </button>
            )}
            {(error || write.error || docs.error) && (
              <p role="alert" className="text-sm text-danger">
                {error ?? write.error ?? docs.error}
              </p>
            )}
            {message && <p role="status">{message}</p>}
          </section>
        )}
        <section className="panel space-y-3 p-4">
          <h2 className="text-lg font-semibold">История версий</h2>
          {history.isError && (
            <p role="alert">
              Не удалось загрузить историю.{" "}
              <button
                className={paymentButton}
                onClick={() => void history.refetch()}
              >
                Повторить
              </button>
            </p>
          )}
          {history.data?.items.map((v) => (
            <details key={v.id} className="rounded-md border p-3 text-sm">
              <summary className="cursor-pointer break-all">
                {formatMoscowDateTime(v.createdAt)} МСК · {v.id} · автор{" "}
                {v.createdBy}
              </summary>
              <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.entries(labels).map(([key, label]) => (
                  <div className="break-words" key={key}>
                    <dt>{label}</dt>
                    <dd>{v[key as keyof PaymentRecipientFields]}</dd>
                  </div>
                ))}
              </dl>
            </details>
          ))}
          <Pagination
            canGoBack={cursorStack.length > 0}
            canGoForward={Boolean(
              history.data?.nextCursor &&
              !cursorStack.includes(history.data.nextCursor),
            )}
            isLoading={history.isFetching}
            onBack={() => setCursorStack((s) => s.slice(0, -1))}
            onForward={() => {
              const next = history.data?.nextCursor;
              if (next && !cursorStack.includes(next))
                setCursorStack((s) => [...s, next]);
            }}
          />
        </section>
      </div>
    </div>
  );
}
